/**
 * rother3 companion — import logic
 * Handles: .json file parsing, share-link data validation, Maps URL parsing
 */

import {
  decodeShareLink as decodeShareLinkFragment,
  decodeFromCurrentHash,
} from './share-link.js';

/**
 * Parse a Google Maps URL to extract Place ID, coordinates, and other info
 * @param {string} url - Google Maps URL
 * @returns {object|null} parsed info or null
 */
export function parseMapsUrl(url) {
  try {
    const parsed = new URL(url);
    const result = {
      originalUrl: url,
      placeId: null,
      coordinates: null,
      businessName: null,
    };

    // Pattern 1: Full place URL with place_id in path
    // https://www.google.com/maps/place/Business+Name/@lat,lng,zoom/data=!3m1!1e3!4m...!1s0x...:0x...
    const placeIdMatch = url.match(/place\/[^/]+\/@[^/]+\/.*?1s(0x[^:]+):(0x[^!]+)/);
    if (placeIdMatch) {
      result.placeId = `${placeIdMatch[1]}:${placeIdMatch[2]}`;
    }

    // Pattern 2: Short maps.app.goo.gl or goo.gl/maps URLs
    if (parsed.hostname.includes('maps.app.goo.gl') || parsed.pathname.includes('/maps/')) {
      // These redirect, we can't resolve without following redirect
      result.placeId = 'short-url';
    }

    // Pattern 3: CID in URL (Customer ID)
    const cidMatch = url.match(/cid=(\d+)/);
    if (cidMatch) {
      result.cid = cidMatch[1];
    }

    // Pattern 4: Coordinates in @lat,lng format
    const coordMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (coordMatch) {
      result.coordinates = {
        lat: parseFloat(coordMatch[1]),
        lng: parseFloat(coordMatch[2]),
      };
    }

    // Pattern 5: Business name from /place/Business+Name
    const nameMatch = url.match(/\/place\/([^/@]+)/);
    if (nameMatch) {
      result.businessName = decodeURIComponent(nameMatch[1].replace(/\+/g, ' '));
    }

    // Pattern 6: Place ID in query param (some URLs have it)
    const queryPlaceId = parsed.searchParams.get('place_id') || parsed.searchParams.get('placeid');
    if (queryPlaceId) {
      result.placeId = queryPlaceId;
    }

    return result;
  } catch {
    return null;
  }
}

/**
 * Decode a share-link URL fragment (base64 in #d=)
 *
 * Delegated to `share-link.js` rather than reimplemented. This file carried its
 * own copy reading `atob(decodeURIComponent(base64))`, which is the decode steps
 * in the **wrong order**: the payload is base64 of UTF-8 *bytes*, so the bytes
 * have to become a string through `decodeURIComponent(escape(atob(...)))`. Running
 * `decodeURIComponent` over the base64 text first throws on the `=` padding and
 * any `+` / `/`, and even when it survives, the resulting mojibake fails
 * `JSON.parse`. So a share link carrying a single non-ASCII character — an en
 * dash in `hours` ("07.30–22.00"), a degree sign, an accented name — decoded to
 * `null` and was reported as "no data in the link", with the real cause silent.
 *
 * Three copies existed and only this one was right. Re-exported under the original
 * name so `app.js`'s imports and the public surface are unchanged.
 *
 * @param {string} url - full URL with fragment
 * @returns {object|null} decoded data or null
 */
export function decodeShareLink(url) {
  return decodeShareLinkFragment(url);
}

/**
 * Parse a local audit JSON file content
 * @param {string} text - file content as string
 * @returns {object|null} decoded data or null
 */
export function parseAuditFile(text) {
  try {
    const data = JSON.parse(text);
    if (data.schemaVersion && data.type) {
      return data;
    }
    throw new Error('Invalid audit file format. Expected schemaVersion and type fields.');
  } catch (err) {
    console.error('[rother3] parse error:', err);
    return null;
  }
}

/**
 * Validate audit data structure
 * @param {object} data - parsed audit data
 * @returns {boolean} true if valid
 */
export function validateAudit(data) {
  if (!data) {return false;}
  if (!data.schemaVersion) {return false;}
  if (!data.type) {return false;}
  if (data.type === 'single-audit' && !data.data) {return false;}
  return true;
}

/**
 * Decode share-link from current page hash
 *
 * Same delegation as `decodeShareLink` above, and for the same reason: this was a
 * third hand-copied instance of the same wrong-order decode.
 *
 * @returns {object|null} decoded data or null
 */
export function decodeShareLinkFromHash() {
  return decodeFromCurrentHash();
}
